import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { superchatAPI, unterschriftAPI } from '@/lib/api';

/* Pop-ups im ganzen CRM (Betreiber 26.10.2026). Beide fragen alle 15 Sekunden nach, beim Start holen sie nur den Zeiger —
   Altes ploppt nie auf. Ist das CRM im Hintergrund und erlaubt der Browser Mitteilungen, kommt zusätzlich eine Systemmitteilung.
   1. „Kunde hat geschrieben“: neue Kundennachrichten im Posteingang (GET /api/superchat/neu), Recht superchat.view, an/aus je
      Gerät im Posteingang (`p1_popup_nachrichten`).
   2. „Upsell unterschrieben“: ein per Link versendeter Upsell wurde am Handy des Mitglieds unterschrieben
      (GET /api/unterschrift/links/unterschrieben), Recht verkauf.create_vertrag, immer an, bleibt eine Minute stehen.
      Steht zusätzlich in der Glocke (auch für alle, die gerade nicht im CRM waren). */
const ABFRAGE_MS = 15000;
export const POPUP_SCHLUESSEL = 'p1_popup_nachrichten';
export function popupAn() { try { return localStorage.getItem(POPUP_SCHLUESSEL) !== 'aus'; } catch { return true; } }
const KANAL = { email: 'E-Mail', whatsapp: 'WhatsApp' };

function mitteilung(titel, text, tag, oeffnen) {
  if (!document.hidden || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const m = new Notification(titel, { body: text || '', tag });
    m.onclick = () => { window.focus(); oeffnen(); m.close(); };
  } catch { /* manche Browser erlauben das nur über einen Service Worker */ }
}

// Fragt `holen(zeiger)` regelmäßig ab; `zeigen(antwort)` nur ab der zweiten Antwort (die erste setzt den Zeiger)
function useAbfrage(darf, holen, zeigen) {
  const zeiger = useRef(null);
  const fn = useRef({ holen, zeigen });
  fn.current = { holen, zeigen };
  useEffect(() => {
    if (!darf) return undefined;
    let aus = false;
    let laeuft = false;
    const abfragen = async () => {
      if (laeuft) return;
      laeuft = true;
      try {
        const r = (await fn.current.holen(zeiger.current)).data;
        const erster = zeiger.current === null;
        zeiger.current = r.zeiger || zeiger.current || '';
        if (!erster && !aus) fn.current.zeigen(r);
      } catch { /* Netz kurz weg oder abgemeldet: nächste Runde */ }
      laeuft = false;
    };
    abfragen();
    const t = setInterval(abfragen, ABFRAGE_MS);
    return () => { aus = true; clearInterval(t); };
  }, [darf]);
}

export default function NeueNachrichtenPopup() {
  const { isAdminPermissions, userPermissions } = useAuth();
  const rechte = userPermissions || {};
  const navigate = useNavigate();
  const gezeigt = useRef(new Set());       // der Zeiger der Unterschriften läuft eine Minute nach: jede nur einmal zeigen

  useAbfrage(isAdminPermissions || !!rechte['superchat.view'], superchatAPI.neu, (r) => {
    if (!popupAn()) return;
    const liste = r.nachrichten || [];
    liste.slice(-3).forEach((n) => {
      const oeffnen = () => navigate(`/posteingang?konv=${encodeURIComponent(n.konversation_id)}`);
      toast(`${n.name} hat geschrieben`, {
        description: `${KANAL[n.kanal] || n.kanal}${n.vorschau ? ` · ${n.vorschau}` : ''}`,
        duration: 20000, action: { label: 'Öffnen', onClick: oeffnen },
      });
      mitteilung(`${n.name} hat geschrieben`, n.vorschau || KANAL[n.kanal], n.konversation_id, oeffnen);
    });
    if (liste.length > 3) {
      toast(`${liste.length - 3} weitere neue Nachrichten`, { duration: 20000, action: { label: 'Posteingang', onClick: () => navigate('/posteingang') } });
    }
  });

  useAbfrage(isAdminPermissions || !!rechte['verkauf.create_vertrag'], unterschriftAPI.unterschrieben, (r) => {
    (r.unterschrieben || []).filter((u) => !gezeigt.current.has(u.id)).forEach((u) => {
      gezeigt.current.add(u.id);
      const titel = `${u.kunde || 'Ein Mitglied'} hat den Upsell unterschrieben`;
      const text = [u.baustein, u.gilt_ab && `gilt ab ${u.gilt_ab}`, u.beitrag].filter(Boolean).join(' · ');
      const oeffnen = () => (u.lead_id ? navigate(`/leads/${u.lead_id}`) : null);
      toast.success(titel, { description: text, duration: 60000, action: u.lead_id ? { label: 'Öffnen', onClick: oeffnen } : undefined });
      mitteilung(titel, text, `upsell-${u.id}`, oeffnen);
    });
  });
  return null;
}
